import React, { useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTrash, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import QRCodeModal from "../QRCodeModal/QRCodeModal";
import Editor from "./Editor/Editor";
import SharedTooltip from "../../shared/Tooltip/SharedTooltip";
import CampaignFeature from "../../Campaigns/CampaignFeature/CampaignFeature";

const ageOptions = [
  { value: 1, label: "Newborn" },
  { value: 2, label: "Young" },
  { value: 3, label: "Adult" },
  { value: 4, label: "Senior" },
  { value: 5, label: "Unknown" },
];
const genderOptions = ["Female", "Male"];
const speciesOptions = ["Dog", "Cat", "Farm", "Exotic", "Other"];

const inputClass = "w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-blue";
const selectClass = "w-full rounded-full border border-gray-300 px-4 py-2.5 text-sm text-gray-700 outline-none focus:border-blue appearance-none bg-white cursor-pointer pr-8";
const labelClass = "block !text-sm !font-semibold text-gray-900 mb-1 text-left";
const errorClass = "text-red-500 !text-xs mt-1";

const Field = ({ label, required, error, children }) => (
  <div className="flex flex-col">
    <label className={labelClass}>
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className={errorClass}>{error}</p>}
  </div>
);

const SelectField = ({ label, required, name, value, onChange, options, placeholder, error }) => (
  <Field label={label} required={required} error={error}>
    <div className="relative">
      <select name={name} value={value} onChange={onChange} className={selectClass}>
        <option value="" disabled>{placeholder}</option>
        {options.map((opt, i) =>
          typeof opt === "string"
            ? <option key={i} value={opt}>{opt}</option>
            : <option key={i} value={opt.value}>{opt.label}</option>
        )}
      </select>
      <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </Field>
);

const AnimalCampaignForm = ({
  nonprofitId,
  onSuccess,
  nonprofitName,
  qrModalOpen,
  campaignUrl,
  setQrModalOpen,
  animalFormValues,
  setAnimalFormValues,
  animalFormErrors,
  setAnimalFormErrors,
  SortableList,
  handleCampaignSubmit,
  handleImageChange,
  handleGraphicImageChange,
  imagePreviews,
  graphicImagePreviews,
  onSortEnd,
  campaignType,
  isLoading,
}) => {
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const finalValue =
      name === "age"
        ? ageOptions.find((o) => o.value.toString() === value)?.value || value
        : value;
    if (name === "donation_goal" && parseFloat(value) < 0) return;
    setAnimalFormValues((prev) => ({ ...prev, [name]: finalValue }));
    setAnimalFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditorChange = (content) => {
    setAnimalFormValues((prev) => ({ ...prev, description: content }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const syntheticEvent = { target: { files: e.dataTransfer.files, name: "images" } };
    handleImageChange(syntheticEvent, setAnimalFormErrors, setAnimalFormValues);
  };

  const removeImage = (index) => {
    setAnimalFormValues((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  return (
    <div>
      <h2 className="!font-bold text-orange !text-2xl text-center mb-6">
        Create New Animal Campaign
      </h2>

      <form onSubmit={handleCampaignSubmit} className="flex flex-col gap-4">
        {/* Animal Name */}
        <Field label="Animal Name" required error={animalFormErrors.animal_name}>
          <input
            type="text"
            name="animal_name"
            placeholder="E.g. Riley"
            value={animalFormValues.animal_name}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        {/* Campaign Title */}
        <Field label="Campaign Title" required error={animalFormErrors.campaign_title}>
          <input
            type="text"
            name="campaign_title"
            placeholder="E.g. Riley Needs Your Help"
            value={animalFormValues.campaign_title}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        {/* Description */}
        <Field label="Description" required error={animalFormErrors.description}>
          <textarea
            name="description"
            placeholder="Write a description..."
            rows={5}
            value={typeof animalFormValues.description === "string" ? animalFormValues.description : ""}
            className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-blue resize-none"
            onChange={(e) => handleEditorChange(e.target.value)}
          />
        </Field>

        {/* Donation Goal + Donation Box Title */}
        <div className="grid grid-cols-2 gap-4">
          <Field label="Donation Goal" required error={animalFormErrors.donation_goal}>
            <input
              type="number"
              name="donation_goal"
              placeholder="E.g. 10"
              value={animalFormValues.donation_goal}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
          <Field
            label={<>Donation Box Title <span className="text-gray-400 font-normal">(max. 30 chars)</span></>}
            required
            error={animalFormErrors.donation_box_title}
          >
            <input
              type="text"
              name="donation_box_title"
              placeholder="E.g. Donate"
              value={animalFormValues.donation_box_title}
              onChange={handleInputChange}
              maxLength={30}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Age + Color */}
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Age"
            required
            name="age"
            value={animalFormValues.age}
            onChange={handleInputChange}
            options={ageOptions}
            placeholder="Select Age"
            error={animalFormErrors.age}
          />
          <Field label="Color" required error={animalFormErrors.color}>
            <input
              type="text"
              name="color"
              placeholder="E.g. Blue"
              value={animalFormValues.color}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
        </div>

        {/* Breed + Gender + Species */}
        <div className="grid grid-cols-3 gap-4">
          <Field label="Breed" required error={animalFormErrors.breed}>
            <input
              type="text"
              name="breed"
              placeholder="E.g. Poodle"
              value={animalFormValues.breed}
              onChange={handleInputChange}
              className={inputClass}
            />
          </Field>
          <SelectField
            label="Gender"
            required
            name="gender"
            value={animalFormValues.gender}
            onChange={handleInputChange}
            options={genderOptions}
            placeholder="Select Gender"
            error={animalFormErrors.gender}
          />
          <SelectField
            label="Species"
            required
            name="species"
            value={animalFormValues.species}
            onChange={handleInputChange}
            options={speciesOptions}
            placeholder="Select Species"
            error={animalFormErrors.species}
          />
        </div>

        {/* Video URLs */}
        <Field label={<>Video URLs <span className="text-gray-400 font-normal">(separated by commas)</span></>}>
          <input
            type="text"
            name="video_urls"
            placeholder="E.g. video1.mov, video2.mov, video3.mov"
            value={animalFormValues.video_urls}
            onChange={handleInputChange}
            className={inputClass}
          />
        </Field>

        {/* Upload Images */}
        <div>
          <label className={labelClass}>
            Upload Images/Graphics <span className="text-gray-400 font-normal">(minimum of 4 images)</span>
            <span className="text-red-500 ml-0.5">*</span>
          </label>

          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center justify-center cursor-pointer transition-colors ${dragOver ? "border-blue bg-blue/5" : "border-gray-300 bg-gray-50"}`}
          >
            <FontAwesomeIcon icon={faUpload} className="text-blue text-2xl mb-3" />
            <p className="text-sm text-gray-700">
              Drag & drop files or{" "}
              <span className="text-blue underline">choose file</span> to upload
            </p>
            <p className="text-xs text-gray-400 mt-1">Supported formates: JPEG, PNG, PSD</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            name="images"
            multiple
            hidden
            onChange={(e) => handleImageChange(e, setAnimalFormErrors, setAnimalFormValues)}
          />

          {/* Uploaded files list */}
          {animalFormValues.images?.length > 0 && (
            <div className="flex flex-col gap-2 mt-3">
              {animalFormValues.images.map((img, i) => (
                <div key={i} className="flex items-center gap-3 border border-gray-200 rounded-xl px-3 py-2">
                  <div className="w-10 h-10 rounded bg-gray-200 flex-shrink-0" />
                  <div className="flex-1 text-left">
                    <p className="!text-sm text-gray-800 font-medium">{img.file?.name || `Image ${i + 1}`}</p>
                    <p className="!text-xs text-green-500 flex items-center gap-1">
                      Uploaded <FontAwesomeIcon icon={faCircleCheck} />
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="text-red-400 hover:text-red-600"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {animalFormErrors.images && (
            <p className={errorClass}>{animalFormErrors.images}</p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setPreviewModalOpen(true); }}
            className="flex-1 bg-blue text-white font-bold py-3 rounded-full hover:opacity-90"
          >
            Preview Campaign
          </button>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className="w-full border-2 border-blue-navy text-blue-navy font-bold py-3 rounded-full hover:bg-gray-50 disabled:opacity-50"
        >
          {isLoading ? "Creating..." : "Create Campaign"}
        </button>
      </form>

      {previewModalOpen && (
        <CampaignFeature
          isOpen={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          campaignData={animalFormValues}
          imagePreviews={imagePreviews}
          graphicImagePreviews={graphicImagePreviews}
          campaignType={campaignType}
        />
      )}

      <QRCodeModal
        isOpen={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        campaignUrl={campaignUrl}
        key={campaignUrl}
      />
    </div>
  );
};

export default AnimalCampaignForm;
